<script setup>
/**
 * OverviewMock — the live event overview, redrawn as crisp vector UI.
 * Mirrors the real dashboard (Bifrost Tech Days: KPI band, registration
 * curve, program column). Sharp at any DPI — stand-in until the
 * scripted retina captures replace it (scripts/capture-product-shots).
 * Decorative: aria-hidden on the wrapper, alt text lives on the section.
 *
 * variant: 'full' (hero) | 'kpis' (close-up used in the live section)
 */
defineProps({
  variant: { type: String, default: 'full' }
})

const CURVE = 'M0 92 C 30 88, 55 80, 85 74 C 120 66, 150 62, 185 52 C 220 42, 250 40, 285 32 C 320 24, 350 22, 380 16'
</script>

<template>
  <div class="bg-white" aria-hidden="true">
    <!-- app bar -->
    <div v-if="variant === 'full'" class="flex items-center gap-4 px-5 py-3 bg-gradient-to-r from-[#5b3f9e] to-[#7a4fb5]">
      <span class="text-white font-semibold text-[15px] tracking-tight">Bifrost<span class="text-[#e8a34d]">.</span></span>
      <div class="flex-1 max-w-lg">
        <div class="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-1.5 text-[12px] text-white/60">
          <svg viewBox="0 0 20 20" fill="none" class="w-3.5 h-3.5"><circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.6" /><path d="M13.5 13.5 17 17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
          Rechercher
        </div>
      </div>
      <div class="hidden sm:flex items-center gap-3 text-white/70 text-[12px]">
        <span>Support</span><span>Utilisateurs</span>
        <span class="w-6 h-6 rounded-full bg-white/20 text-white text-[10px] font-semibold flex items-center justify-center">FN</span>
      </div>
    </div>

    <!-- event header -->
    <div v-if="variant === 'full'" class="flex flex-wrap items-center justify-between gap-3 px-5 pt-4 pb-3 border-b border-slate-100">
      <div>
        <div class="flex items-center gap-2.5">
          <span class="text-[17px] font-semibold text-slate-900">Bifrost Tech Days</span>
          <span class="inline-flex items-center gap-1.5 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> LIVE
          </span>
        </div>
        <div class="flex flex-wrap gap-1.5 mt-1.5 text-[11px] text-slate-500">
          <span class="border border-slate-200 rounded px-1.5 py-0.5">Présentiel</span>
          <span class="border border-slate-200 rounded px-1.5 py-0.5">Palais de la Culture, Alger</span>
          <span class="border border-slate-200 rounded px-1.5 py-0.5">Congrès</span>
        </div>
      </div>
      <div class="flex items-center gap-2 text-[11px]">
        <span class="text-slate-400"><span class="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 me-1.5"></span>Temps réel · 19:32</span>
        <span class="bg-[#6d4fc2] text-white rounded-md px-3 py-1.5 font-medium">Actualiser</span>
      </div>
    </div>

    <!-- KPI band -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-100" :class="variant === 'kpis' ? '' : 'border-b border-slate-100'">
      <div class="bg-white px-5 py-4">
        <div class="text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1.5">Inscrits</div>
        <div class="text-2xl font-semibold text-slate-900 tabular-nums" dir="ltr">60</div>
        <div class="text-[10.5px] text-slate-400 mt-1" dir="ltr"><span class="text-emerald-600 font-medium">+8</span> au pic, le 29 juin</div>
      </div>
      <div class="bg-white px-5 py-4">
        <div class="text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1.5">Taux de présence</div>
        <div class="text-2xl font-semibold text-slate-900 tabular-nums" dir="ltr">78,3<span class="text-sm text-slate-400">%</span></div>
        <div class="mt-2 h-1 rounded-full bg-slate-100 overflow-hidden" dir="ltr">
          <div class="h-full w-[78%] rounded-full bg-[#6d4fc2]"></div>
        </div>
      </div>
      <div class="bg-white px-5 py-4">
        <div class="text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1.5">Sur site</div>
        <div class="flex items-baseline gap-1.5">
          <span class="text-2xl font-semibold text-slate-900 tabular-nums" dir="ltr">37</span>
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
        </div>
        <div class="text-[10.5px] text-slate-400 mt-1">présents à l'instant</div>
      </div>
      <div class="bg-white px-5 py-4">
        <div class="text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1.5">Participations</div>
        <div class="text-2xl font-semibold text-slate-900 tabular-nums" dir="ltr">50</div>
        <div class="text-[10.5px] text-slate-400 mt-1" dir="ltr">2 activités en cours</div>
      </div>
    </div>

    <!-- curve + program -->
    <div class="grid lg:grid-cols-[1.6fr_1fr] gap-px bg-slate-100" :class="variant === 'kpis' ? 'border-t border-slate-100' : ''">
      <div class="bg-white px-5 py-4">
        <div class="flex items-center justify-between mb-3">
          <div>
            <div class="text-[13px] font-semibold text-slate-900">Dynamique d'inscriptions</div>
            <div class="text-[10.5px] text-slate-400">Volume cumulé et quotidien</div>
          </div>
          <div class="flex items-center gap-3 text-[10px] text-slate-400">
            <span class="flex items-center gap-1"><span class="w-3 h-0.5 bg-[#6d4fc2] rounded"></span> Cumulé</span>
            <span class="flex items-center gap-1"><span class="w-2 h-2 bg-[#e6ddfa] rounded-sm"></span> Par jour</span>
          </div>
        </div>
        <svg viewBox="0 0 380 100" class="w-full" preserveAspectRatio="none" style="height: 110px" dir="ltr">
          <g stroke="#f1f5f9" stroke-width="1">
            <line x1="0" y1="25" x2="380" y2="25" /><line x1="0" y1="55" x2="380" y2="55" /><line x1="0" y1="85" x2="380" y2="85" />
          </g>
          <g fill="#ede9fb">
            <rect x="18" y="86" width="7" height="10" rx="1" /><rect x="58" y="82" width="7" height="14" rx="1" />
            <rect x="98" y="78" width="7" height="18" rx="1" /><rect x="138" y="84" width="7" height="12" rx="1" />
            <rect x="178" y="72" width="7" height="24" rx="1" /><rect x="218" y="80" width="7" height="16" rx="1" />
            <rect x="258" y="68" width="7" height="28" rx="1" /><rect x="298" y="76" width="7" height="20" rx="1" />
            <rect x="338" y="62" width="7" height="34" rx="1" />
          </g>
          <path :d="CURVE + ' L380 100 L0 100 Z'" fill="url(#ovm-fill)" />
          <path :d="CURVE" stroke="#6d4fc2" stroke-width="2" fill="none" stroke-linecap="round" />
          <defs>
            <linearGradient id="ovm-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#6d4fc2" stop-opacity="0.16" />
              <stop offset="1" stop-color="#6d4fc2" stop-opacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div class="bg-white px-5 py-4">
        <div class="flex items-center justify-between mb-3">
          <span class="text-[13px] font-semibold text-slate-900">Programme</span>
          <span class="text-[10.5px] text-slate-400" dir="ltr">6 sessions</span>
        </div>
        <div class="space-y-3">
          <div class="flex gap-3">
            <span class="text-[11px] font-mono text-slate-400 pt-0.5 tabular-nums" dir="ltr">11:15</span>
            <div class="min-w-0">
              <div class="text-[12px] text-slate-800 leading-snug truncate">Accueil & enregistrement</div>
              <div class="text-[10.5px] text-slate-400">Auditorium Asgard · <span class="text-emerald-600">Terminée</span></div>
            </div>
          </div>
          <div class="flex gap-3">
            <span class="text-[11px] font-mono text-slate-400 pt-0.5 tabular-nums" dir="ltr">12:15</span>
            <div class="min-w-0">
              <div class="text-[12px] text-slate-800 leading-snug truncate">Keynote — la plateforme unifiée</div>
              <div class="text-[10.5px] text-slate-400">Auditorium Asgard · <span class="text-emerald-600">Terminée</span></div>
            </div>
          </div>
          <div class="flex gap-3">
            <span class="text-[11px] font-mono text-slate-400 pt-0.5 tabular-nums" dir="ltr">14:15</span>
            <div class="min-w-0">
              <div class="text-[12px] text-slate-800 leading-snug truncate">Table ronde — l'expérience participant</div>
              <div class="text-[10.5px] text-slate-400">Salle Midgard · <span class="text-[#6d4fc2]">En cours</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
